// IMPORT REACT
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// IMPORT CSS
import "../../../theme/style.scss";
import "react-phone-number-input/style.css";

// IMPORT UI COMPONENTS
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CustomBreadcrumb from "../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import ErrorIcon from "@mui/icons-material/Error";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";

// IMPORT SERVICE
import { AlertType } from "../../../services/models/AlertType";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import { PreferencesContext } from "../../../PreferenceContext";
import { PAGE_SIZE } from "../../../services/common/const";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";
import { CustomTextField } from "../../../components/CustomFields/TextField";
import { CustomSelect } from "../../../components/CustomFields/CustomSelect";

import {
  CreateDiagnosis,
  GetDiagnosisDetail,
  UpdateDiagnosis,
} from "../../../services/DiagnosisService";
import { GetDepartmentList } from "../../../services/DepartmentService";
import { GetSpecialityList } from "../../../services/SpecialityService";
import DiagnosisSeverityToggle from "../../../components/Animated/DiagnosisSeverityToggle";
import { diagnosisOptions } from "../../../shared/Model/OPD/Diagnosis";

// SCHEMA FOR OPTION
interface selectOption {
  label: string;
  value: string;
}
// SCHEMA FOR FORM
interface formInitialValueTypes {
  diagnosisId: number;
  code: string;
  diagnosisName: string;
  departmentId: selectOption | null;
  specialityId: selectOption | null;
  isDeleted: boolean;
  diagnosisSeverityLevel: selectOption | null;
}

// SCHEMA FOR RESULTDATA
interface ResultData {
  diagnosisTypeColl: Array<any>;
}
// SCHEMA FOR PARAMETER
interface Parameter {
  diagnosisId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
}
// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  diagnosisId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
};

export default function DiagnosisMasterSave(props: any) {
  // PROPS
  const { isForPopUp = false, handleClosePopUp, name } = props;

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let curRawDiagnosisMasterId = urlParams.get("q");
  let curDiagnosisMasterId = 0;
  if (
    curRawDiagnosisMasterId !== null &&
    curRawDiagnosisMasterId !== undefined &&
    !isForPopUp
  ) {
    let decId = edService.decrypt(curRawDiagnosisMasterId);
    if (decId !== "" && decId !== null && decId !== undefined) {
      curDiagnosisMasterId = +decId;
    }
  }
  const [param, setParam] = useState(parameters);
  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isloading, setIsLoading] = useState<boolean>(true);
  const [departmentData, setdepartmentData] = useState<any[]>([]);
  const [specialityData, setspecialityData] = useState<any[]>([]);
  const [selectedDiagnosis, setSelectedDiagnosis] = useState("");

  // DEFAULT SCHEMA VALUE FOR FORM
  const initDiagnosisMasterData: formInitialValueTypes = {
    diagnosisId: 0,
    code: "",
    diagnosisName: isForPopUp ? name?.toUpperCase() : "",
    isDeleted: false,
    departmentId:
      userContext?.departmentId > 0
        ? {
            label: userContext?.departmentName || "",
            value: userContext?.departmentId?.toString(),
          }
        : null,
    specialityId:
      userContext?.specialityId > 0
        ? {
            label: userContext?.specialityName || "",
            value: userContext?.specialityId?.toString(),
          }
        : null,
    diagnosisSeverityLevel: {
      label: "Mild",
      value: "MILD",
    },
  };
  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    diagnosisId: Yup.number().nullable(),
    code: Yup.string(),
    diagnosisName: Yup.string().required("Diagnosis Name is required"),
    departmentId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Department is required"),
    specialityId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Speciality is required"),
    diagnosisSeverityLevel: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Diagnosis Severity Level is required"),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initDiagnosisMasterData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  // GET DETAIL
  const getDiagnosisMasterDetailById = async () => {
    let diagnosisMaster: any;
    let params = new URLSearchParams();
    params.append("DiagnosisId", curDiagnosisMasterId.toString());
    params.append("ConsumerId", userContext.consumerId.toString());
    params.append("OrgId", userContext.organizationId.toString());
    params.append("BranchId", userContext.branchId.toString());

    try {
      const res: any = await GetDiagnosisDetail(params);
      diagnosisMaster = res.data.data;
      const selectedOption = diagnosisOptions.find(
        (x) => x.value === diagnosisMaster?.diagnosisSeverityLevel,
      );
      methods.reset({
        diagnosisId: diagnosisMaster?.diagnosisId || 0,
        code: diagnosisMaster?.code || "",
        diagnosisName: diagnosisMaster?.diagnosisName || "",
        departmentId: diagnosisMaster?.departmentId
          ? {
              value: diagnosisMaster?.departmentId?.toString(),
              label: diagnosisMaster?.departmentName || "",
            }
          : null,
        specialityId: diagnosisMaster?.specialityId
          ? {
              label: diagnosisMaster?.specialityName,
              value: diagnosisMaster?.specialityId,
            }
          : null,
        diagnosisSeverityLevel: diagnosisMaster?.diagnosisSeverityLevel
          ? {
              label: selectedOption?.label || "",
              value: selectedOption?.value || "",
            }
          : null,
      });
      return diagnosisMaster;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE CONSUMER
  const saveDiagnosisMaster = async (value: formInitialValueTypes) => {
    try {
      let result: any = "";
      let formData = {
        diagnosisId: value.diagnosisId,
        ConsumerId: userContext.consumerId.toString(),
        OrgId: userContext.organizationId.toString(),
        BranchId: userContext.branchId.toString(),
        code: value.code?.trim(),
        diagnosisName: value?.diagnosisName.trim() || "",
        departmentId: value?.departmentId?.value || null,
        specialityId: value?.specialityId?.value || null,
        DataManagedBy: userContext.loginDetail.userId,
        diagnosisSeverityLevel: value?.diagnosisSeverityLevel?.value || null,
      };
      if (curDiagnosisMasterId > 0) {
        result = await UpdateDiagnosis(formData);
      } else {
        result = await CreateDiagnosis(formData);
      }
      if (result) {
        if (!isForPopUp) {
          navigate(-1);
        } else {
          if (result?.data) {
            // Ensure the data structure matches what the parent component expects
            const diagnosisData = {
              diagnosisId:
                result?.data?.diagnosisId || result?.data?.value || 0,
              diagnosisName:
                value?.diagnosisName?.trim() ||
                result?.data?.diagnosisName ||
                result?.data?.label ||
                "",
              value: result?.data?.diagnosisId || result?.data?.value || 0,
              label:
                value?.diagnosisName?.trim() ||
                result?.data?.diagnosisName ||
                result?.data?.label ||
                "",
            };
            handleClosePopUp("CREATE", diagnosisData);
          }
        }
      }
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    saveDiagnosisMaster(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (!isForPopUp) {
      navigate(-1);
    } else {
      handleClosePopUp("CLOSE");
    }
  };

  // GET DEPARTMENT
  async function fetchDepartment() {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext?.consumerId?.toString());
      params.append("orgId", userContext?.organizationId?.toString());
      params.append("branchId", userContext?.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      params.append("mode", "DROPDOWN".toString());
      const departmentData: any = await GetDepartmentList(params);
      if (departmentData?.data?.data?.departmentColl) {
        let departmentCollection = departmentData.data.data.departmentColl;
        setdepartmentData(departmentCollection);
      }
      // setIsLoading(false);
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
      // setIsLoading(false);
    }
  }
  // GET SPECIALITY
  async function fetchSpeciality(departmentIds: string) {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("departmentIds", departmentIds);
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      params.append("mode", "DROPDOWN".toString());
      const specialityData: any = await GetSpecialityList(params);
      if (specialityData?.data?.data?.specialityColl) {
        let specialityCollection = specialityData.data.data.specialityColl;
        setspecialityData(specialityCollection);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FETCH DATA
  const fetchData = async () => {
    try {
      if (curDiagnosisMasterId <= 0) {
        await fetchDepartment();
      }
      if (curDiagnosisMasterId > 0) {
        await getDiagnosisMasterDetailById();
        // await fetchSpeciality(Doctor?.departmentId);
      }
      // else if (userContext?.departmentId > 0) {
      //   await fetchSpeciality(userContext?.departmentId?.toString());
      //   if (userContext?.specialityId > 0) {
      //     await getDiagnosisMasterDetailById();
      //   }
      // }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [curDiagnosisMasterId]);

  return (
    <Box>
      <Box className={"main-container"}>
        {isForPopUp === false && (
          <>
            <Grid container className="page-header">
              <Grid item xs={6} sm={6} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    height: "100%",
                    alignItems: "center",
                  }}
                >
                  <CustomBreadcrumb />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6} md={6}>
                <Box className="page-header-icon">
                  <Button
                    color="primary"
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    onClick={handleBack}
                    startIcon={<ArrowBackIcon />}
                  >
                    Back
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} className={"group-container"}>
                <Grid item container xs={12} spacing={2}>
                  {/* <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.code
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Code
                      </FormLabel>
                      <TextField
                        placeholder=""
                        autoCapitalize="none"
                        type="text"
                        fullWidth
                        size="small"
                        {...methods.register("code", {
                          required: true,
                        })}
                        error={!!methods.formState.errors.code}
                        className={`text-input ${methods.formState.errors.code ? "error" : ""
                          }`}
                      />
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.code?.message}
                    </Typography>
                  </Grid>
                  <Grid item sm={9}></Grid> */}
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.departmentId
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Department *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isloading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="departmentId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"departmentId"}
                              FieldName={"departmentId"}
                              options={departmentData}
                              isDisabled={curDiagnosisMasterId > 0}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.departmentId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "departmentId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                  methods.setValue("specialityId", null, {
                                    shouldValidate: true,
                                  });

                                  if (e?.value) {
                                    setIsLoading(true);
                                    await fetchSpeciality(e.value);
                                    setIsLoading(false);
                                  } else {
                                    setspecialityData([]);
                                  }
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.departmentId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.specialityId
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Speciality *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isloading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="specialityId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"specialityId"}
                              FieldName={"specialityId"}
                              options={specialityData}
                              isDisabled={curDiagnosisMasterId > 0}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.specialityId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "specialityId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.specialityId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.diagnosisName
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Diagnosis Name *
                      </FormLabel>
                      <TextField
                        placeholder="Ex. URTI, LRTI"
                        autoCapitalize="none"
                        type="text"
                        fullWidth
                        size="small"
                        autoFocus
                        {...methods.register("diagnosisName", {
                          required: true,
                        })}
                        error={!!methods.formState.errors.diagnosisName}
                        className={`text-input ${
                          methods.formState.errors.diagnosisName ? "error" : ""
                        }`}
                      />
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.diagnosisName?.message}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    lg={6}
                    sx={{
                      display: {
                        xs: "none",
                        sm: "none",
                        md: "none",
                        lg: "block",
                      },
                    }}
                  ></Grid>
                  <Grid item xs={12} sm={12} md={12} lg={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.diagnosisSeverityLevel
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Diagnosis Severity Level *
                      </FormLabel>
                      <Controller
                        name="diagnosisSeverityLevel"
                        control={methods.control}
                        render={({ field }) => (
                          <DiagnosisSeverityToggle
                            value={field.value?.value || ""}
                            options={diagnosisOptions}
                            onChange={(val) => {
                              field.onChange(
                                diagnosisOptions.find((x) => x.value === val),
                              );
                            }}
                          />
                        )}
                      />

                      <Typography className="validation-message">
                        {
                          methods.formState.errors.diagnosisSeverityLevel
                            ?.message
                        }
                      </Typography>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                  >
                    {curDiagnosisMasterId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    onClick={handleBack}
                  >
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    </Box>
  );
}
